import { v4 as uuidv4 } from 'uuid';

/**
 * Service for device fingerprinting and web crypto operations
 * This service can be used with any authentication method to capture device information
 */
class DeviceWebCryptoService {
  private readonly storageKeyPrefix = 'strategiz_device_key_';
  private readonly deviceInfoKey = 'strategiz_device_info';
  
  /**
   * Generates a new device key pair for authentication
   * @returns Promise<CryptoKeyPair>
   */
  async generateKeyPair(): Promise<CryptoKeyPair> {
    try {
      // Generate a new key pair using the Web Crypto API
      const keyPair = await window.crypto.subtle.generateKey(
        {
          name: 'ECDSA',
          namedCurve: 'P-256',
        },
        true, // extractable
        ['sign', 'verify'] // key usages
      );
      
      return keyPair;
    } catch (error) {
      console.error('Failed to generate device key pair:', error);
      throw new Error('Failed to generate device key pair');
    }
  }
  
  /**
   * Exports a public key to a base64 string
   * @param publicKey The public key to export
   * @returns Promise<string>
   */
  async exportPublicKey(publicKey: CryptoKey): Promise<string> {
    try {
      const exported = await window.crypto.subtle.exportKey('spki', publicKey);
      return this.arrayBufferToBase64(exported);
    } catch (error) {
      console.error('Failed to export public key:', error);
      throw new Error('Failed to export public key');
    }
  }
  
  /**
   * Imports a public key from a base64 string
   * @param publicKeyBase64 The base64-encoded public key
   * @returns Promise<CryptoKey>
   */
  async importPublicKey(publicKeyBase64: string): Promise<CryptoKey> {
    try {
      const keyData = this.base64ToArrayBuffer(publicKeyBase64);
      
      return await window.crypto.subtle.importKey(
        'spki',
        keyData,
        {
          name: 'ECDSA',
          namedCurve: 'P-256',
        },
        true,
        ['verify']
      );
    } catch (error) {
      console.error('Failed to import public key:', error);
      throw new Error('Failed to import public key');
    }
  }
  
  /**
   * Signs data with a private key
   * @param privateKey The private key to sign with
   * @param data The data to sign
   * @returns Promise<string>
   */
  async signData(privateKey: CryptoKey, data: string): Promise<string> {
    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      
      const signature = await window.crypto.subtle.sign(
        {
          name: 'ECDSA',
          hash: { name: 'SHA-256' },
        },
        privateKey,
        dataBuffer
      );
      
      return this.arrayBufferToBase64(signature);
    } catch (error) {
      console.error('Failed to sign data:', error);
      throw new Error('Failed to sign data');
    }
  }
  
  /**
   * Verifies a signature
   * @param publicKey The public key to verify with
   * @param signature The signature to verify
   * @param data The original data
   * @returns Promise<boolean>
   */
  async verifySignature(publicKey: CryptoKey, signature: string, data: string): Promise<boolean> {
    try {
      const encoder = new TextEncoder();
      const dataBuffer = encoder.encode(data);
      const signatureBuffer = this.base64ToArrayBuffer(signature);
      
      return await window.crypto.subtle.verify(
        {
          name: 'ECDSA',
          hash: { name: 'SHA-256' },
        },
        publicKey,
        signatureBuffer,
        dataBuffer
      );
    } catch (error) {
      console.error('Failed to verify signature:', error);
      return false;
    }
  }
  
  /**
   * Stores a key pair in localStorage with a unique device ID
   * @param keyPair The key pair to store
   * @returns Promise<string> The device ID
   */
  async storeDeviceKeyPair(keyPair: CryptoKeyPair): Promise<string> {
    try {
      const deviceId = uuidv4();
      const privateKeyJwk = await window.crypto.subtle.exportKey('jwk', keyPair.privateKey);
      const publicKeyJwk = await window.crypto.subtle.exportKey('jwk', keyPair.publicKey);
      
      // Store the key pair in localStorage
      localStorage.setItem(
        `${this.storageKeyPrefix}${deviceId}`,
        JSON.stringify({
          privateKey: privateKeyJwk,
          publicKey: publicKeyJwk,
          createdAt: Date.now(),
        })
      );
      
      return deviceId;
    } catch (error) {
      console.error('Failed to store device key pair:', error);
      throw new Error('Failed to store device key pair');
    }
  }
  
  /**
   * Retrieves a key pair from localStorage by device ID
   * @param deviceId The device ID
   * @returns Promise<CryptoKeyPair | null>
   */
  async getDeviceKeyPair(deviceId: string): Promise<CryptoKeyPair | null> {
    try {
      const storedData = localStorage.getItem(`${this.storageKeyPrefix}${deviceId}`);
      
      if (!storedData) {
        return null;
      }
      
      const { privateKey: privateKeyJwk, publicKey: publicKeyJwk } = JSON.parse(storedData);
      
      // Import the keys from JWK format
      const privateKey = await window.crypto.subtle.importKey(
        'jwk',
        privateKeyJwk,
        {
          name: 'ECDSA',
          namedCurve: 'P-256',
        },
        true,
        ['sign']
      );
      
      const publicKey = await window.crypto.subtle.importKey(
        'jwk',
        publicKeyJwk,
        {
          name: 'ECDSA',
          namedCurve: 'P-256',
        },
        true,
        ['verify']
      );
      
      return { privateKey, publicKey };
    } catch (error) {
      console.error('Failed to retrieve device key pair:', error);
      return null;
    }
  }
  
  /**
   * Gets the current device ID from localStorage
   * @returns string | null
   */
  getCurrentDeviceId(): string | null {
    // Look for any stored device keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(this.storageKeyPrefix)) {
        return key.substring(this.storageKeyPrefix.length);
      }
    }
    
    return null;
  }
  
  /**
   * Collects device information for fingerprinting
   * @returns Object containing device information
   */
  collectDeviceInfo(): any {
    const deviceInfo: any = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelRatio: window.devicePixelRatio,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: new Date().getTimezoneOffset(),
      localStorage: !!window.localStorage,
      sessionStorage: !!window.sessionStorage,
      cookiesEnabled: navigator.cookieEnabled,
      timestamp: new Date().toISOString(),
    };
    
    // Add canvas fingerprint
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (ctx) {
        canvas.width = 200;
        canvas.height = 50;
        
        // Draw text with different styles
        ctx.textBaseline = 'top';
        ctx.font = '14px Arial';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('Strategiz', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('Fingerprint', 4, 30);
        
        // Get canvas data and hash it
        const canvasData = canvas.toDataURL();
        deviceInfo.canvasFingerprint = this.simpleHash(canvasData);
      }
    } catch (e) {
      deviceInfo.canvasFingerprint = 'not-supported';
    }
    
    return deviceInfo;
  }
  
  /**
   * Stores device information in localStorage
   * @param userId The user ID to associate with the device info
   * @returns The device information object
   */
  storeDeviceInfo(userId: string): any {
    try {
      // Collect device information
      const deviceInfo = this.collectDeviceInfo();
      deviceInfo.userId = userId;
      deviceInfo.lastSeen = new Date().toISOString();
      
      // Store in localStorage
      localStorage.setItem(this.deviceInfoKey, JSON.stringify(deviceInfo));
      
      return deviceInfo;
    } catch (error) {
      console.error('Failed to store device info:', error);
      return null;
    }
  }
  
  /**
   * Gets the stored device information
   * @returns The device information object or null
   */
  getDeviceInfo(): any | null {
    try {
      const storedInfo = localStorage.getItem(this.deviceInfoKey);
      return storedInfo ? JSON.parse(storedInfo) : null;
    } catch (error) {
      console.error('Failed to get device info:', error);
      return null;
    }
  }
  
  /**
   * Updates the device information with the current user ID
   * @param userId The user ID to associate with the device info
   * @returns The updated device information object
   */
  updateDeviceInfoForUser(userId: string): any {
    const deviceInfo = this.getDeviceInfo() || this.collectDeviceInfo();
    deviceInfo.userId = userId;
    deviceInfo.lastSeen = new Date().toISOString();
    
    // Store the updated info
    localStorage.setItem(this.deviceInfoKey, JSON.stringify(deviceInfo));
    
    return deviceInfo;
  }
  
  /**
   * Authenticates with the device key
   * @param email The user's email
   * @returns Promise<{ success: boolean, email: string, deviceId: string, signature: string, deviceInfo: any }>
   */
  async authenticate(email: string): Promise<{ 
    success: boolean, 
    email: string, 
    deviceId: string, 
    signature: string,
    deviceInfo: any
  }> {
    try {
      // Check if we have a device ID
      let deviceId = this.getCurrentDeviceId();
      let keyPair: CryptoKeyPair | null = null;
      
      // If we don't have a device ID, generate a new key pair
      if (!deviceId) {
        keyPair = await this.generateKeyPair();
        deviceId = await this.storeDeviceKeyPair(keyPair);
      } else {
        // Otherwise, retrieve the existing key pair
        keyPair = await this.getDeviceKeyPair(deviceId);
        
        if (!keyPair) {
          // If we couldn't retrieve the key pair, generate a new one
          keyPair = await this.generateKeyPair();
          deviceId = await this.storeDeviceKeyPair(keyPair);
        }
      }
      
      // Create a challenge to sign
      const challenge = `${email}:${Date.now()}`;
      
      // Sign the challenge with the private key
      const signature = await this.signData(keyPair.privateKey, challenge);
      
      // Collect and store device information
      const deviceInfo = this.storeDeviceInfo(email);
      
      return {
        success: true,
        email,
        deviceId,
        signature,
        deviceInfo
      };
    } catch (error) {
      console.error('Device web crypto authentication failed:', error);
      throw new Error('Device web crypto authentication failed');
    }
  }
  
  /**
   * Helper method to convert an ArrayBuffer to a base64 string
   * @param buffer The ArrayBuffer to convert
   * @returns string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    
    return window.btoa(binary);
  }
  
  /**
   * Helper method to convert a base64 string to an ArrayBuffer
   * @param base64 The base64 string to convert
   * @returns ArrayBuffer
   */
  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = window.atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    
    return bytes.buffer;
  }
  
  /**
   * Simple hash function for strings
   * @param str The string to hash
   * @returns A hash string
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    return hash.toString(36);
  }
}

// Create a singleton instance
const deviceWebCryptoService = new DeviceWebCryptoService();

export default deviceWebCryptoService;
